import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Key } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      toast.success("Profil sačuvan");
      refresh?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingProfile(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("Nove lozinke se ne poklapaju");
    if (pw.new_password.length < 6) return toast.error("Nova lozinka mora imati bar 6 karaktera");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Lozinka promenjena");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingPw(false);
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="overline">Nalog</div>
        <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Moj profil</h1>
        <p className="text-ink-muted mt-2">Izmeni svoje podatke i lozinku.</p>
      </div>

      <form onSubmit={saveProfile} className="surface-card p-6 space-y-4" data-testid="profile-form">
        <div className="flex items-center gap-2 overline"><User className="w-3 h-3" /> Osnovne informacije</div>
        <div>
          <label className="text-sm font-medium">Ime i prezime</label>
          <input
            data-testid="profile-name"
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Email</label>
          <input
            data-testid="profile-email"
            type="email"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
          />
        </div>
        <button disabled={savingProfile} className="btn-primary" data-testid="save-profile-btn">
          {savingProfile ? "Čuvam..." : "Sačuvaj profil"}
        </button>
      </form>

      <form onSubmit={changePassword} className="surface-card p-6 space-y-4" data-testid="password-form">
        <div className="flex items-center gap-2 overline"><Key className="w-3 h-3" /> Promena lozinke</div>
        <div>
          <label className="text-sm font-medium">Trenutna lozinka</label>
          <input
            data-testid="password-old"
            type="password"
            value={pw.old_password}
            onChange={(e) => setPw({ ...pw, old_password: e.target.value })}
            className="w-full mt-1 px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Nova lozinka (min. 6)</label>
            <input
              data-testid="password-new"
              type="password"
              minLength={6}
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Potvrdi novu lozinku</label>
            <input
              data-testid="password-confirm"
              type="password"
              minLength={6}
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="w-full mt-1 px-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
              required
            />
          </div>
        </div>
        <button disabled={savingPw} className="btn-primary" data-testid="save-password-btn">
          {savingPw ? "Menjam..." : "Promeni lozinku"}
        </button>
      </form>
    </div>
  );
}
